<template>
  <div class="custom-dropdown" ref="dropdownRef">
    <div 
      class="dropdown-trigger"
      @click="toggleDropdown"
      :class="{ 'is-open': isOpen, 'has-value': modelValue }"
      ref="triggerRef"
    >
      <div class="dropdown-content">
        <span v-if="selectedOption" class="selected-value">
          {{ selectedOption[optionLabel] }}
        </span>
        <span v-else class="placeholder">
          {{ placeholder }}
        </span>
      </div>
      <div class="dropdown-actions">
        <button 
          v-if="showClear && modelValue" 
          @click.stop="clearSelection"
          class="clear-button"
          type="button"
        >
          <i class="pi pi-times"></i>
        </button>
        <div class="dropdown-arrow">
          <i class="pi pi-chevron-down"></i>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Portal the dropdown panel to body -->
  <teleport to="body">
    <transition name="dropdown-slide">
      <div 
        v-if="isOpen" 
        class="dropdown-panel-portal"
        :style="panelStyle"
        ref="panelRef"
      >
        <div class="dropdown-items">
          <div 
            v-for="option in options" 
            :key="option[optionValue]"
            class="dropdown-item"
            :class="{ 'is-selected': modelValue === option[optionValue] }"
            @click="selectOption(option)"
          >
            <span class="item-text">{{ option[optionLabel] }}</span>
            <i v-if="modelValue === option[optionValue]" class="pi pi-check item-check"></i>
          </div>
          <div v-if="options.length === 0" class="dropdown-empty">
            No options available
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';

interface Option {
  label: string;
  value: any;
  [key: string]: any;
}

interface Props {
  modelValue?: string | number | null;
  options?: Option[];
  optionLabel?: string;
  optionValue?: string;
  placeholder?: string;
  showClear?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  options: () => [],
  optionLabel: 'label',
  optionValue: 'value',
  placeholder: 'Select an option',
  showClear: true
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number | null];
  'change': [value: string | number | null];
}>();

const dropdownRef = ref(null);
const triggerRef = ref(null);
const panelRef = ref(null);
const isOpen = ref(false);
const panelStyle = ref({});

const selectedOption = computed((): Option | undefined => {
  if (!props.modelValue) return undefined;
  return props.options.find((option: Option) => option[props.optionValue] === props.modelValue);
});

const updatePanelPosition = async (): Promise<void> => {
  if (!triggerRef.value || !isOpen.value) return;
  
  await nextTick();
  
  const triggerRect = (triggerRef.value as HTMLElement).getBoundingClientRect();
  const scrollY = window.scrollY || window.pageYOffset;
  const scrollX = window.scrollX || window.pageXOffset;
  
  panelStyle.value = {
    position: 'absolute',
    top: `${triggerRect.bottom + scrollY + 8}px`,
    left: `${triggerRect.left + scrollX}px`,
    width: `${triggerRect.width}px`,
    zIndex: '99999'
  };
};

const toggleDropdown = async (): Promise<void> => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    await updatePanelPosition();
  }
};

const selectOption = (option: Option): void => {
  const value = option[props.optionValue];
  emit('update:modelValue', value);
  emit('change', { value, originalEvent: null });
  isOpen.value = false;
};

const clearSelection = (): void => {
  emit('update:modelValue', null);
  emit('change', { value: null, originalEvent: null });
};

const closeDropdown = (event: Event): void => {
  if (dropdownRef.value && !(dropdownRef.value as HTMLElement).contains(event.target as Node) && 
      panelRef.value && !(panelRef.value as HTMLElement).contains(event.target as Node)) {
    isOpen.value = false;
  }
};

const handleResize = (): void => {
  if (isOpen.value) {
    updatePanelPosition();
  }
};

const handleScroll = (): void => {
  if (isOpen.value) {
    updatePanelPosition();
  }
};

// Watch for position updates when panel opens
watch(isOpen, (newValue: boolean) => {
  if (newValue) {
    updatePanelPosition();
  }
});

onMounted(() => {
  document.addEventListener('click', closeDropdown);
  window.addEventListener('resize', handleResize);
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown);
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped>
.custom-dropdown {
  position: relative;
  width: 100%;
  isolation: isolate;
  z-index: 1000;
}

.dropdown-trigger {
  background: #ffffff;
  border: 2px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.08);
  position: relative;
  overflow: hidden;
}

.dropdown-trigger::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent);
  transition: left 0.6s ease;
}

.dropdown-trigger:hover::before {
  left: 100%;
}

.dropdown-trigger:hover {
  border-color: rgba(102, 126, 234, 0.4);
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.15);
  transform: translateY(-2px);
}

.dropdown-trigger.is-open {
  border-color: #667eea;
  box-shadow: 
    0 8px 32px rgba(102, 126, 234, 0.2),
    0 0 0 3px rgba(102, 126, 234, 0.1);
}

.dropdown-content {
  flex: 1;
  display: flex;
  align-items: center;
}

.selected-value {
  color: #2c3e50;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.2px;
}

.placeholder {
  color: #8e9aaf;
  font-style: italic;
  font-size: 14px;
}

.dropdown-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.clear-button {
  background: none;
  border: none;
  color: #8e9aaf;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.clear-button:hover {
  background: rgba(231, 76, 60, 0.1);
  color: #e74c3c;
}

.dropdown-arrow {
  color: #667eea;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.dropdown-trigger.is-open .dropdown-arrow {
  transform: rotate(180deg);
}

.dropdown-panel-portal {
  background: #ffffff;
  border: 2px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  box-shadow: 
    0 16px 48px rgba(0, 0, 0, 0.12),
    0 8px 24px rgba(102, 126, 234, 0.08);
  overflow: hidden;
  z-index: 99999 !important;
}

.dropdown-items {
  max-height: 240px;
  overflow-y: auto;
  padding: 8px;
}

.dropdown-items::-webkit-scrollbar {
  width: 6px;
}

.dropdown-items::-webkit-scrollbar-track {
  background: transparent;
}

.dropdown-items::-webkit-scrollbar-thumb {
  background: rgba(102, 126, 234, 0.3);
  border-radius: 3px;
}

.dropdown-items::-webkit-scrollbar-thumb:hover {
  background: rgba(102, 126, 234, 0.5);
}

.dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 12px;
  margin: 2px 0;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 500;
  color: #2c3e50;
  border: 1px solid transparent;
  position: relative;
  overflow: hidden;
}

.dropdown-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 3px;
  height: 100%;
  background: #667eea;
  transform: scaleY(0);
  transition: transform 0.2s ease;
}

.dropdown-item:hover {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.05) 100%);
  border-color: rgba(102, 126, 234, 0.2);
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
}

.dropdown-item:hover::before {
  transform: scaleY(1);
}

.dropdown-item.is-selected {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.1) 100%);
  color: #667eea;
  font-weight: 700;
  border-color: rgba(102, 126, 234, 0.3);
  box-shadow: 
    0 4px 16px rgba(102, 126, 234, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
}

.dropdown-item.is-selected::before {
  transform: scaleY(1);
}

.item-text {
  flex: 1;
}

.item-check {
  color: #667eea;
  font-weight: bold;
  animation: checkIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-empty {
  padding: 16px;
  text-align: center;
  color: #8e9aaf;
  font-style: italic;
  background: rgba(102, 126, 234, 0.02);
  border-radius: 12px;
  margin: 8px;
}

/* Transitions */
.dropdown-slide-enter-active,
.dropdown-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-slide-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.dropdown-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

@keyframes checkIn {
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
